// src/pages/CreditCardPayableDetail.tsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

import { apiGet, apiPost } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { AppleDateInput } from "../components/AppleDateInput";
import { AppleNumberInput } from "../components/AppleNumberInput";
import { Button } from "../components/Button";
import EmptyState from "../components/EmptyState";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal, formatAnyDateToLocale } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatNumber";
import { A, pillClass } from "../styles/appleClasses";
import { extractApiDetailOutcome, isClosedPeriodError } from "../utils/closedPeriod";
import {
  canonicalCreditCardBillPayment,
  classifyCreditCardBillPaymentFailure,
  decideCreditCardBillPaymentAttempt,
  fingerprintCreditCardBillPayment,
  IDLE_CREDIT_CARD_BILL_PAYMENT_ATTEMPT,
  randomUuidKey,
  resolveCreditCardBillPaymentAmbiguousFailure,
  resolveCreditCardBillPaymentDefiniteRejection,
  resolveCreditCardBillPaymentSuccess,
  shouldRestoreAmbiguousAttempt,
  type CreditCardBillPaymentAttempt,
} from "../utils/creditCardBillPaymentIdempotency";

// Local authored outcomes remain language-neutral; API detail remains raw.
type PageError =
  | { kind: "key"; key: "directory.customers.noCompany"
      | "creditCardPayable.loadError"
      | "creditCardPayable.selectBankAccount"
      | "creditCardPayable.positiveAmount"
      | "creditCardPayable.overpayment"
      | "creditCardPayable.editedRetry"
      | "creditCardPayable.crossCompanyRetry"
      | "creditCardPayable.paymentConflict"
      | "sales.errors.generic"
      | "purchases.errors.recordPayment" }
  | { kind: "closed-period" }
  | { kind: "raw"; text: string };
class MissingCardError extends Error {}
function apiPageError(err: unknown, fallback: "creditCardPayable.loadError" | "purchases.errors.recordPayment"): PageError {
  if (err instanceof MissingCardError) return { kind: "key", key: "creditCardPayable.loadError" };
  const outcome = extractApiDetailOutcome(err);
  if (outcome.kind === "generic") return { kind: "key", key: "sales.errors.generic" };
  return outcome.text ? outcome : { kind: "key", key: fallback };
}

type SettlementPayable = {
  account_id: number;
  name: string;
  kind: string;
  balance: number;
};

type Account = {
  id: number;
  name: string;
  type?: string | null;
  kind?: string | null;
  is_system?: boolean | null;
};

type ActivityRow = {
  date: string;
  type:
    | "Expense"
    | "Purchase"
    | "Payment"
    | "Deposit"
    | "Deposit refund"
    | "Prepaid"
    | "Prepaid refund"
    | "Fixed asset"
    | "Voided expense"
    | "Voided purchase"
    | "Voided deposit"
    | "Voided prepaid"
    | "Voided fixed asset"
    | "Activity";
  source: string;
  detail: string;
  amount: number;
};

function lower(v: unknown) {
  return String(v ?? "")
    .toLowerCase()
    .trim();
}

function money(n: number | null | undefined) {
  return formatMoney(Number(n || 0));
}

function ymdDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function activityDescription(row: ActivityRow, typeLabel: string) {
  const base = [row.source, row.detail].filter(Boolean).join(" - ");

  if (row.type === "Voided expense" || row.type === "Voided purchase") {
    return [typeLabel, base].filter(Boolean).join(" - ");
  }

  return base || typeLabel;
}

export default function CreditCardPayableDetail() {
  const { t } = useTranslation();
  const renderError = (value: PageError) => value.kind === "raw" ? value.text
    : value.kind === "closed-period"
      ? t("sales.errors.closedSubmit", { date: t("sales.errors.closedDate") })
      : t(value.key);
  const activityTypeLabel = (type: string): string => {
    switch (type) {
      case "Expense": return t("depositDetail.expenseTab");
      case "Payment": return t("sales.common.payment");
      case "Purchase": return t("creditCardPayable.activityTypes.purchase");
      case "Deposit": return t("creditCardPayable.activityTypes.deposit");
      case "Deposit refund": return t("creditCardPayable.activityTypes.depositRefund");
      case "Prepaid": return t("creditCardPayable.activityTypes.prepaid");
      case "Prepaid refund": return t("creditCardPayable.activityTypes.prepaidRefund");
      case "Fixed asset": return t("creditCardPayable.activityTypes.fixedAsset");
      case "Voided expense": return t("creditCardPayable.activityTypes.voidedExpense");
      case "Voided purchase": return t("creditCardPayable.activityTypes.voidedPurchase");
      case "Voided deposit": return t("creditCardPayable.activityTypes.voidedDeposit");
      case "Voided prepaid": return t("creditCardPayable.activityTypes.voidedPrepaid");
      case "Voided fixed asset": return t("creditCardPayable.activityTypes.voidedFixedAsset");
      case "Activity": return t("creditCardPayable.activityTypes.activity");
      default: return type;
    }
  };
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const today = ymdTodayLocal();
  const defaultFromDate = ymdDaysAgo(30);

  const [summary, setSummary] = useState<SettlementPayable | null>(null);
  const [bankAccounts, setBankAccounts] = useState<Account[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);
  const [error, setError] = useState<PageError | null>(null);

  const [paymentBankId, setPaymentBankId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(today);
  const [paymentMemo, setPaymentMemo] = useState("");
  const [paymentError, setPaymentError] = useState<PageError | null>(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [balanceReady, setBalanceReady] = useState<string | null>(null);
  const [summaryRefreshing, setSummaryRefreshing] = useState(false);
  const [summaryRefreshWarning, setSummaryRefreshWarning] = useState(false);
  const [activityRefreshWarning, setActivityRefreshWarning] = useState(false);
  const summaryBusyRef = useRef(false);
  const activityBusyRef = useRef(false);
  const balanceReadyRef = useRef<string | null>(null);
  const verifiedSummaryRef = useRef<SettlementPayable | null>(null);
  const eligibleAccountsRef = useRef<Account[]>([]);
  const summaryGenerationRef = useRef(0);
  const activityGenerationRef = useRef(0);
  const companyGenerationRef = useRef(0);
  const mountedRef = useRef(true);
  const recordedCompanyRef = useRef<string | null>(null);
  const disqualifiedSelectionRef = useRef(false);

  // Credit Card Bill Payment idempotency attempt lifecycle (see
  // utils/creditCardBillPaymentIdempotency.ts). A ref, not state: must be
  // stable across renders, must not itself trigger a re-render, and must
  // be readable synchronously by the in-flight guard below. Scoped to a
  // single CreditCardBillPaymentAttempt (state + companyId + key +
  // fingerprint + submitted payload together) rather than independent
  // refs, so the lifecycle can never observe an invalid partial
  // combination. This route is keyed only by `:company_id` (no card
  // identifier -- there is exactly one Credit Card Payable account per
  // company), and this component is NOT remounted by a `:company_id`
  // route-param change alone (same <Route> element, same component
  // instance) -- e.g. switching companies via in-app navigation -- so an
  // ambiguous attempt from a previously viewed company must persist and
  // stay correctly scoped rather than being silently dropped or reused.
  const paymentAttemptRef = useRef<CreditCardBillPaymentAttempt>(
    IDLE_CREDIT_CARD_BILL_PAYMENT_ATTEMPT,
  );
  // Purely synchronous double-click/repeat-tap guard -- independent of the
  // attempt state machine above.
  const paymentInFlightRef = useRef(false);

  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(today);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD";

  // These snapshots also reject responses in the render-to-effect interval.
  const scopeRef = useRef({ company_id, fromDate, toDate });
  scopeRef.current = { company_id, fromDate, toDate };

  useLayoutEffect(() => {
    mountedRef.current = true;
    companyGenerationRef.current += 1;
    summaryGenerationRef.current += 1;
    balanceReadyRef.current = null;
    verifiedSummaryRef.current = null;
    eligibleAccountsRef.current = [];
    recordedCompanyRef.current = null;
    disqualifiedSelectionRef.current = false;
    setBalanceReady(null);
    summaryBusyRef.current = false;
    activityBusyRef.current = false;
    setSummaryRefreshing(false);
    setSummaryRefreshWarning(false);
    setActivityRefreshWarning(false);
    setPaymentError(null);
    return () => {
      mountedRef.current = false;
      balanceReadyRef.current = null;
      verifiedSummaryRef.current = null;
      summaryGenerationRef.current += 1;
      activityGenerationRef.current += 1;
    };
  }, [company_id]);

  useLayoutEffect(() => {
    activityGenerationRef.current += 1;
  }, [company_id, fromDate, toDate]);

  const loadSummaryAndAccounts = async () => {
    const company = scopeRef.current.company_id;
    if (!company) return;
    const companyGeneration = companyGenerationRef.current;
    const generation = ++summaryGenerationRef.current;
    const current = () => mountedRef.current &&
      scopeRef.current.company_id === company &&
      companyGenerationRef.current === companyGeneration &&
      summaryGenerationRef.current === generation;
    balanceReadyRef.current = null;
    verifiedSummaryRef.current = null;
    setBalanceReady(null);
    summaryBusyRef.current = true;
    setSummaryRefreshing(true);
    try {
      const [settlementRows, accounts] = await Promise.all([
        apiGet<SettlementPayable[]>(
          `/ar-ap/settlement-payables?company_id=${company}`,
        ),
        apiGet<Account[]>(`/accounts?company_id=${company}`),
      ]);
      if (!current()) return;
      const card = (settlementRows || []).find(
        (row) => lower(row.kind) === "credit_card_payable",
      );
      if (!card) throw new MissingCardError("Failed to load Credit Card Payable.");
      const banks = (accounts || [])
        .filter((account) => lower(account.type) === "asset" &&
          ["cash", "bank"].includes(lower(account.kind)) && !account.is_system)
        .sort((a, b) => a.name.localeCompare(b.name));
      setSummary(card);
      setBankAccounts(banks);
      eligibleAccountsRef.current = banks;
      setPaymentBankId((previous) => {
        if (previous) {
          if (banks.some((account) => String(account.id) === previous)) return previous;
          disqualifiedSelectionRef.current = true;
          return "";
        }
        return !disqualifiedSelectionRef.current && banks[0] ? String(banks[0].id) : "";
      });
      verifiedSummaryRef.current = card;
      balanceReadyRef.current = company;
      setBalanceReady(company);
      setSummaryRefreshWarning(false);
    } catch (err) {
      if (!current()) return;
      if (recordedCompanyRef.current === company) setSummaryRefreshWarning(true);
      throw err;
    } finally {
      if (current()) {
        summaryBusyRef.current = false;
        setSummaryRefreshing(false);
      }
    }
  };

  const loadActivity = async (paymentRefresh = false) => {
    const { company_id: company, fromDate: from, toDate: to } = scopeRef.current;
    if (!company) return;
    const companyGeneration = companyGenerationRef.current;
    const generation = ++activityGenerationRef.current;
    const current = () => mountedRef.current &&
      scopeRef.current.company_id === company &&
      scopeRef.current.fromDate === from && scopeRef.current.toDate === to &&
      companyGenerationRef.current === companyGeneration &&
      activityGenerationRef.current === generation;
    activityBusyRef.current = true;
    setActivityLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const rows = await apiGet<ActivityRow[]>(
        `/ar-ap/credit-card-payable/activity?${params.toString()}`,
      );
      if (!current()) return;
      setActivity(rows || []);
      setActivityRefreshWarning(false);
    } catch (err) {
      if (!current()) return;
      if (paymentRefresh) setActivityRefreshWarning(true);
      else setActivity([]);
      throw err;
    } finally {
      if (current()) {
        activityBusyRef.current = false;
        setActivityLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!company_id) {
      setError({ kind: "key", key: "directory.customers.noCompany" });
      setLoading(false);
      return;
    }
    const companyGeneration = companyGenerationRef.current;
    setLoading(true);
    const summaryLoad = loadSummaryAndAccounts();
    const generation = summaryGenerationRef.current;
    const current = () => mountedRef.current &&
      companyGenerationRef.current === companyGeneration &&
      summaryGenerationRef.current === generation;
    Promise.all([summaryLoad, loadActivity()])
      .then(() => { if (current()) setError(null); })
      .catch((err) => {
        if (current()) setError(apiPageError(err, "creditCardPayable.loadError"));
      })
      .finally(() => { if (current()) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id]);

  useEffect(() => {
    // The loader owns publication and warnings; this effect only consumes rejection.
    loadActivity().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const retrySummaryRefresh = () => {
    if (summaryBusyRef.current || paymentInFlightRef.current) return;
    // GET only: never enter the repayment attempt lifecycle.
    loadSummaryAndAccounts().catch(() => {});
  };
  const retryActivityRefresh = () => {
    if (activityBusyRef.current) return;
    loadActivity(true).catch(() => {});
  };

  const selectedBank = useMemo(
    () =>
      bankAccounts.find((account) => String(account.id) === paymentBankId) ||
      null,
    [bankAccounts, paymentBankId],
  );

  const balance = Number(summary?.balance || 0);
  const status = balance > 0 ? t("creditCardPayable.outstanding") : t("purchases.status.paid");
  const statusClass = balance > 0 ? pillClass("warning") : pillClass("success");

  // Restore an unresolved ambiguous attempt's exact submitted values when
  // company_id resolves to (or changes back to) the company it belongs
  // to. This component is never remounted by a `:company_id` route-param
  // change alone (same <Route> element, same component instance), so an
  // in-app company switch reuses paymentAttemptRef as-is. Without this,
  // the form would show either stale values from the previously viewed
  // company or fresh defaults that don't match the retained fingerprint,
  // silently trapping the user behind an "edited retry" block with no way
  // to make a safe, unchanged retry. Mirrors FixedAssetDetail.tsx's
  // openPayment restore branch, adapted to this page's always-visible
  // (non-dialog) payment section.
  useEffect(() => {
    if (!company_id) return;
    const companyIdNum = Number(company_id);
    const attempt = paymentAttemptRef.current;
    if (!shouldRestoreAmbiguousAttempt(attempt, companyIdNum)) return;

    const payload = attempt.submittedPayload!;
    setPaymentDate(payload.date);
    setPaymentBankId(String(payload.bank_account_id));
    setPaymentAmount(payload.amount);
    setPaymentMemo(payload.memo || "");
  }, [company_id]);

  const submitPayment = async () => {
    // Synchronous guard against two immediate invocations (e.g. a double
    // click before React re-renders the disabled button) resulting in two
    // POSTs -- paymentSaving alone can't catch this since it only updates
    // on the next render.
    if (paymentInFlightRef.current) return;

    if (!company_id) return;
    const companyIdNum = Number(company_id);
    const submittedCompany = company_id;
    const submittedCompanyGeneration = companyGenerationRef.current;
    const currentCompany = () => mountedRef.current &&
      scopeRef.current.company_id === submittedCompany &&
      companyGenerationRef.current === submittedCompanyGeneration;
    if (balanceReadyRef.current !== company_id) return;
    const amount = Number(paymentAmount);
    if (!paymentBankId || !eligibleAccountsRef.current.some(
      (account) => String(account.id) === paymentBankId,
    )) {
      setPaymentError({ kind: "key", key: "creditCardPayable.selectBankAccount" });
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentError({ kind: "key", key: "creditCardPayable.positiveAmount" });
      return;
    }
    if (amount > Number(verifiedSummaryRef.current?.balance || 0)) {
      setPaymentError({ kind: "key", key: "creditCardPayable.overpayment" });
      return;
    }

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether this is a first attempt (mint a new
    // key), an unchanged retry of a still-ambiguous SAME-company attempt
    // (reuse its key), an edited retry of one (block, no network call), or
    // a retry against a DIFFERENT company while an ambiguous attempt is
    // still outstanding (block, no network call, no silent key rotation).
    const canonicalPayload = canonicalCreditCardBillPayment(
      paymentDate,
      paymentBankId,
      amount,
      paymentMemo,
    );
    const fingerprint = fingerprintCreditCardBillPayment(canonicalPayload);
    const decision = decideCreditCardBillPaymentAttempt(
      paymentAttemptRef.current,
      companyIdNum,
      canonicalPayload,
      fingerprint,
      randomUuidKey,
    );

    if (decision.kind === "blocked-mismatch") {
      // The previous attempt under this key may already have been
      // recorded -- never silently mint a new key or retry with edited
      // details under the same one. Block client-side, make no network
      // call. Deliberately does NOT tell the user that reloading alone is
      // safe (it isn't -- a reload says nothing about whether the prior
      // attempt posted); instead point at restoring the original values
      // (which matches the retained fingerprint and allows a safe retry
      // with the same key) or checking Credit Card Activity below to see
      // whether the prior attempt already posted before starting a new
      // payment.
      setPaymentError({ kind: "key", key: "creditCardPayable.editedRetry" });
      return;
    }
    if (decision.kind === "blocked-cross-company") {
      setPaymentError({ kind: "key", key: "creditCardPayable.crossCompanyRetry" });
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- paymentInFlightRef.current already
      // returns above before this point. Kept for correctness of the pure
      // decision function itself.
      return;
    }

    paymentAttemptRef.current = decision.attempt;
    paymentInFlightRef.current = true;

    setPaymentSaving(true);
    setPaymentError(null);
    try {
      try {
        // Only apiPost rejection (including response parsing) classifies repayment failure.
        await apiPost("/ar-ap/credit-card-payments", {
          ...canonicalPayload,
          idempotency_key: decision.idempotencyKey,
        });
      } catch (err: any) {
        const failureClass = classifyCreditCardBillPaymentFailure(err, isClosedPeriodError);

        if (failureClass === "closed-period") {
          // Definite pre-commit rejection: assert_year_open raises before any
          // claim/business write commits, so nothing was persisted. Clear the
          // attempt so a corrected date can retry under a new key.
          paymentAttemptRef.current = resolveCreditCardBillPaymentDefiniteRejection();
          if (currentCompany()) setPaymentError({ kind: "closed-period" });
          return;
        }

        if (failureClass === "idempotency-conflict") {
          // Definite: this key is permanently bound to a different,
          // already-completed request. Retrying under it can never succeed
          // for this payload -- clear it so the next attempt mints a fresh
          // one.
          paymentAttemptRef.current = resolveCreditCardBillPaymentDefiniteRejection();
          if (currentCompany()) setPaymentError({ kind: "key", key: "creditCardPayable.paymentConflict" });
          return;
        }

        if (failureClass === "definite-rejection") {
          // HTTP 400/404 business or request validation failure -- nothing
          // persisted. Clear the attempt so the user can edit and retry
          // under a new key.
          paymentAttemptRef.current = resolveCreditCardBillPaymentDefiniteRejection();
          if (currentCompany()) setPaymentError(apiPageError(err, "purchases.errors.recordPayment"));
          return;
        }

        // Ambiguous (network failure, timeout, unreadable response, HTTP
        // 500, or anything else unrecognized): retain the key, fingerprint,
        // and submitted payload so an unchanged retry reuses the same key
        // and safely replays instead of risking a duplicate; an edited
        // retry -- or a retry against a different company -- stays blocked
        // above.
        paymentAttemptRef.current = resolveCreditCardBillPaymentAmbiguousFailure(
          paymentAttemptRef.current,
        );
        if (currentCompany()) setPaymentError(apiPageError(err, "purchases.errors.recordPayment"));

        return;
      }
      paymentAttemptRef.current = resolveCreditCardBillPaymentSuccess();
      if (!mountedRef.current || scopeRef.current.company_id !== submittedCompany) return;
      // Invalidate synchronously before any render or refresh can permit another POST.
      summaryGenerationRef.current += 1;
      balanceReadyRef.current = null;
      verifiedSummaryRef.current = null;
      setBalanceReady(null);
      recordedCompanyRef.current = submittedCompany;
      if (currentCompany()) {
        setPaymentAmount("");
        setPaymentMemo("");
      }
      const refreshCompanyGeneration = companyGenerationRef.current;
      // Each loader owns its warning. Neither rejection can reclassify the POST.
      await Promise.all([
        loadSummaryAndAccounts().catch(() => {}),
        loadActivity(true).catch(() => {}),
      ]);
      if (mountedRef.current && scopeRef.current.company_id === submittedCompany &&
        companyGenerationRef.current === refreshCompanyGeneration) setLoading(false);
    } finally {
      paymentInFlightRef.current = false;
      if (mountedRef.current) setPaymentSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`text-center py-6 text-gray-600 ${A.body}`}>
        {t("creditCardPayable.loading")}
      </div>
    );
  }

  if (error) return <EmptyState message={renderError(error)} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;
  if (!company_id) return <EmptyState message={t("directory.customers.noCompany")} heading={t("directory.common.nothingHere")} backLabel={t("directory.common.goBack")} />;

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${A.body}`}
    >
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-5xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/payables"))}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`${A.title} text-center flex-1 tracking-tight`}>
          {t("payables.creditCardPayable")}
        </h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <main className="w-full max-w-5xl px-2 pb-10 space-y-4">
        <section className={`${A.card} p-5`}>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className={`${A.label} mt-1`}>{t("creditCardPayable.currentBalance")}</div>
              {balanceReady === company_id && (
                <div className={`${A.body} ${A.num} font-medium text-gray-700`}>
                  {money(balance)}
                </div>
              )}
            </div>
            {balanceReady === company_id && (
              <span className={`${statusClass} self-start`}>{status}</span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={`block ${A.label} mb-1`}>{t("sales.common.paymentDate")}</label>
              <AppleDateInput
                valueYmd={paymentDate}
                onChangeYmd={setPaymentDate}
                allowEmpty={false}
                ariaLabel={t("purchases.detail.paymentDateAria")}
                openCalendarLabel={t("sales.shared.openCalendar")}
                className={A.num}
              />
            </div>

            <div>
              <label className={`block ${A.label} mb-1`}>{t("purchases.detail.paymentAccount")}</label>
              <AppleComboBox<Account>
                options={bankAccounts}
                value={selectedBank}
                onChange={(account) =>
                  setPaymentBankId(account ? String(account.id) : "")
                }
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
                getOptionLabel={(account) => (account ? account.name : "")}
                placeholder={
                  bankAccounts.length ? t("purchases.detail.selectPaymentAccount") : t("creditCardPayable.noBankAccounts")
                }
              />
            </div>

            <div>
              <label className={`block ${A.label} mb-1`}>{t("sales.common.amount")}</label>
              <AppleNumberInput
                className={`${A.inputBase} text-right ${A.num}`}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="md:col-span-3">
              <label className={`block ${A.label} mb-1`}>{t("sales.common.memo")}</label>
              <input
                type="text"
                className={A.inputBase}
                value={paymentMemo}
                onChange={(e) => setPaymentMemo(e.target.value)}
                placeholder={t("sales.detail.optional")}
              />
            </div>
          </div>

          {summaryRefreshWarning && (
            <div className="mt-3 text-[13px] text-amber-700" role="status">
              <p>{t("creditCardPayable.summaryRefreshWarning")}</p>
              <Button type="button" variant="secondary"
                disabled={summaryRefreshing || paymentSaving} onClick={retrySummaryRefresh}>
                {t("creditCardPayable.refreshBalanceAndAccounts")}
              </Button>
            </div>
          )}

          {paymentError && (
            <div className="mt-3 text-[13px] font-medium text-red-600">
              {renderError(paymentError)}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button
              type="button"
              variant="primary"
              disabled={paymentSaving || balanceReady !== company_id || balance <= 0}
              onClick={submitPayment}
            >
              {paymentSaving ? t("directory.common.savingDots") : t("purchases.detail.addPayment")}
            </Button>
          </div>
        </section>

        <section className={`${A.card} p-5`}>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className={`${A.label} font-medium`}>
                {t("creditCardPayable.activityTitle")}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={`block ${A.label} mb-1`}>{t("sales.shared.from")}</label>
                <AppleDateInput
                  valueYmd={fromDate}
                  onChangeYmd={setFromDate}
                  allowEmpty
                  ariaLabel={t("creditCardPayable.fromDateAria")}
                openCalendarLabel={t("sales.shared.openCalendar")}
                  className={A.num}
                />
              </div>
              <div>
                <label className={`block ${A.label} mb-1`}>{t("sales.shared.to")}</label>
                <AppleDateInput
                  valueYmd={toDate}
                  onChangeYmd={setToDate}
                  allowEmpty
                  ariaLabel={t("creditCardPayable.toDateAria")}
                openCalendarLabel={t("sales.shared.openCalendar")}
                  className={A.num}
                />
              </div>
            </div>
          </div>

          {activityRefreshWarning && (
            <div className="mb-3 text-[13px] text-amber-700" role="status">
              <p>{t("creditCardPayable.activityRefreshWarning")}</p>
              <Button type="button" variant="secondary"
                disabled={activityLoading} onClick={retryActivityRefresh}>
                {t("creditCardPayable.refreshActivity")}
              </Button>
            </div>
          )}
          {activityLoading ? (
            <div className="py-6 text-center text-[13px] text-gray-500">
              {t("creditCardPayable.activityLoading")}
            </div>
          ) : activityRefreshWarning ? null : activity.length === 0 ? (
            <div className="py-6 text-center text-[13px] text-gray-500">
              {t("creditCardPayable.activityEmpty")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-[13px] text-gray-600">
                <thead>
                  <tr
                    className={`${A.label} border-b border-gray-100 text-left`}
                  >
                    <th className="py-2 pr-3 w-28 font-medium">{t("sales.common.date")}</th>
                    <th className="py-2 pr-3 font-medium">{t("sales.edit.description")}</th>
                    <th className="py-2 text-right w-28 font-medium">{t("sales.common.amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((row, idx) => (
                    <tr
                      key={`${row.date}-${row.type}-${idx}`}
                      className="border-b border-gray-50"
                    >
                      <td
                        className={`py-2 pr-3 whitespace-nowrap text-gray-600 ${A.num}`}
                      >
                        {formatAnyDateToLocale(row.date)}
                      </td>
                      <td className="py-2 pr-3 truncate text-gray-600">
                        {activityDescription(row, activityTypeLabel(row.type))}
                      </td>
                      <td className={`py-2 text-right text-gray-600 ${A.num}`}>
                        {money(row.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
